import { makeAutoObservable } from "mobx"
import { TASK_DOCKED_PANE_WIDTH } from "../../constants"

const DOCKED_PANE_WIDTH_STORAGE_KEY = "wildlark:docked-pane-width:v1"

export const DOCKED_PANE_MIN_WIDTH_PX = 384
export const DOCKED_PANE_MAX_WIDTH_PX = 832
export const DOCKED_PANE_MIN_PRIMARY_WIDTH_PX = 320

export function dockedPaneWidthBounds(viewportWidth: number): { min: number; max: number } {
    return {
        min: DOCKED_PANE_MIN_WIDTH_PX,
        max: Math.max(DOCKED_PANE_MIN_WIDTH_PX, Math.min(DOCKED_PANE_MAX_WIDTH_PX, Math.floor(viewportWidth - DOCKED_PANE_MIN_PRIMARY_WIDTH_PX))),
    }
}

function clampDockedPaneWidth(width: number, viewportWidth: number): number {
    const { min, max } = dockedPaneWidthBounds(viewportWidth)
    return Math.round(Math.min(max, Math.max(min, width)))
}

function readStoredWidth(): number | null {
    if (typeof window === "undefined") return null
    try {
        const serialized = window.localStorage.getItem(DOCKED_PANE_WIDTH_STORAGE_KEY)
        if (serialized === null) return null
        const width = Number(serialized)
        return Number.isFinite(width) && width > 0 ? Math.round(width) : null
    } catch {
        return null
    }
}

function persistWidth(width: number): void {
    if (typeof window === "undefined") return
    try {
        window.localStorage.setItem(DOCKED_PANE_WIDTH_STORAGE_KEY, String(width))
    } catch {
        return
    }
}

function clearPersistedWidth(): void {
    if (typeof window === "undefined") return
    try {
        window.localStorage.removeItem(DOCKED_PANE_WIDTH_STORAGE_KEY)
    } catch {
        return
    }
}

class DockedPaneWidthStore {
    widthPx: number | null = readStoredWidth()
    isResizing = false

    constructor() {
        makeAutoObservable(this)
    }

    get cssWidth(): string {
        if (this.widthPx === null) return TASK_DOCKED_PANE_WIDTH
        return `clamp(${DOCKED_PANE_MIN_WIDTH_PX}px, ${this.widthPx}px, min(${DOCKED_PANE_MAX_WIDTH_PX}px, calc(100vw - ${DOCKED_PANE_MIN_PRIMARY_WIDTH_PX}px)))`
    }

    currentWidth(viewportWidth: number): number {
        const preferredWidth = this.widthPx ?? viewportWidth * 0.42
        return clampDockedPaneWidth(preferredWidth, viewportWidth)
    }

    setWidth(width: number, viewportWidth: number): void {
        const nextWidth = clampDockedPaneWidth(width, viewportWidth)
        this.widthPx = nextWidth
        persistWidth(nextWidth)
    }

    beginResize(): void {
        this.isResizing = true
    }

    previewWidth(width: number, viewportWidth: number): void {
        this.widthPx = clampDockedPaneWidth(width, viewportWidth)
    }

    finishResize(): void {
        this.isResizing = false
        if (this.widthPx !== null) persistWidth(this.widthPx)
    }

    reset(): void {
        this.widthPx = null
        this.isResizing = false
        clearPersistedWidth()
    }
}

export const dockedPaneWidthStore = new DockedPaneWidthStore()
